import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import ConfirmModal from '../components/ConfirmModal';
import WordPressOrphanModal from '../components/WordPressOrphanModal';
import { DSButton, DSCard, DSPageHeader } from '../design-system/primitives';
import './AutomationSettings.css';

function AutomationSettings({ embedded = false }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [orphanState, setOrphanState] = useState(null);

  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchStatus();
    }
  }, [isAdmin]);

  const fetchStatus = async () => {
    try {
      const response = await apiService.get('/automation/status');

      // The endpoint answers { eventAutomation: { eventGeneration, hostReminders,
      // wordpressSync }, taskAutomation, wordpressApi }. This page read
      // `status.eventGeneration` straight off the top level, which is always undefined -
      // so every scheduler reported "Inactive" whether it was running or not. Flatten
      // once, here, rather than repeating the path at every use.
      const data = response.data || {};

      setStatus({
        ...(data.eventAutomation || {}),
        wordpressApi: data.wordpressApi || {}
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching status:', err);
      setError('Could not read automation status. It may be rate limited; try again shortly.');
    }
  };


  const handleGenerateEvents = () => {
    setConfirmState({
      title: 'Generate Events?',
      message: 'Generate events from all active series now?',
      confirmLabel: 'Generate',
      onConfirm: async () => {
        setProcessing('generate');
        setError(null);
        setSuccess(null);

        try {
          const response = await apiService.post('/automation/generate-events');
          const { eventsCreated, seriesProcessed, skippedSeries } = response.data;

          // A bare "generated 0 events" gives no way to tell "nothing was due" from
          // "the series you care about was skipped".
          let message = eventsCreated > 0
            ? `Generated ${eventsCreated} event${eventsCreated === 1 ? '' : 's'} from ${seriesProcessed} series`
            : `No new events were due across ${seriesProcessed} series`;

          if (skippedSeries?.length > 0) {
            message += `. Skipped (auto-generate off): ${skippedSeries.join(', ')}`;
          }

          setSuccess(message);
          setConfirmState(null);
        } catch (err) {
          setError('Failed to generate events: ' + (err.response?.data?.error || err.message));
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const handleCheckReminders = async () => {
    setProcessing('reminders');
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.post('/automation/check-reminders');
      setSuccess(`Sent ${response.data.remindersSent} reminders`);
      setProcessing(null);
    } catch (err) {
      setError('Failed to check reminders: ' + (err.response?.data?.error || err.message));
      setProcessing(null);
    }
  };

  const handleWordPressSync = async () => {
    setProcessing('wpsync');
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.post('/automation/sync-wordpress');
      const { created, updated, failed, errors } = response.data;
      
      let message = `WordPress sync complete: ${created} created, ${updated} updated`;
      if (failed > 0) {
        message += `, ${failed} failed`;
        console.error('Sync errors:', errors);
      }
      
      setSuccess(message);
      setProcessing(null);
      fetchStatus(); // Refresh status
    } catch (err) {
      setError('Failed to sync to WordPress: ' + (err.response?.data?.error || err.message));
      setProcessing(null);
    }
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Finds WordPress events that no longer exist here, then hands the list to the admin to
  // pick from. Never deletes on the strength of the scan alone: an event added by hand on
  // the site is indistinguishable from a leftover, and a series delete leaves a dozen at
  // once, so the choice has to be per event rather than all or nothing.
  const handleWordPressOrphans = async () => {
    setProcessing('orphans');
    setError(null);
    setSuccess(null);

    try {
      const response = await apiService.get('/automation/wordpress-orphans');
      const { orphans, scanned } = response.data;

      if (!orphans || orphans.length === 0) {
        setSuccess(`Checked ${scanned} WordPress events: nothing orphaned`);
        setProcessing(null);
        return;
      }

      setOrphanState({ orphans, scanned, submitting: false });
    } catch (err) {
      setError('Could not check WordPress: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleRemoveOrphans = async (ids) => {
    setOrphanState((current) => ({ ...current, submitting: true }));

    try {
      const result = await apiService.post('/automation/wordpress-orphans/delete', {
        wordpress_post_ids: ids
      });
      setOrphanState(null);
      setSuccess(`Removed ${result.data.deleted} event(s) from WordPress`);
    } catch (err) {
      setOrphanState(null);
      setError('Failed to remove those events: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCopyEnvVar = async () => {
    try {
      await navigator.clipboard.writeText('WP_API_ENABLED=true');
      setSuccess('Copied WP_API_ENABLED=true');
    } catch (err) {
      setError('Could not copy to the clipboard');
    }
  };

  if (!isAdmin) {
    return (
      <div className="automation-settings">
        <div className="error">You must be an admin to access this page.</div>
      </div>
    );
  }

  return (
    <div className={`automation-settings${embedded ? ' embedded' : ''}`}>
      {!embedded && (
        <DSPageHeader title="Automation & WordPress Integration" />
      )}

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Status is a list, not a grid of cards. Three one-line facts do not each need
          their own card, and as cards they filled a screen saying almost nothing. */}
      {status && (
        <DSCard frame="bars" flush headerTone="strong" cornerVariant="md" title="Status" className="automation-status">
          {[
            {
              key: 'generation',
              label: 'Event generation',
              on: Boolean(status.eventGeneration?.running),
              tone: status.eventGeneration?.running ? 'on' : 'idle',
              state: status.eventGeneration?.running ? 'On' : 'Idle',
              detail: status.eventGeneration?.running
                ? (status.eventGeneration?.lastRun
                  ? `Last run ${formatDate(status.eventGeneration.lastRun)}`
                  : 'Scheduled, not run yet')
                : 'Never run. Series will not create events on their own.'
            },
            {
              key: 'reminders',
              label: 'Host reminders',
              on: Boolean(status.hostReminders?.running),
              tone: status.hostReminders?.running ? 'on' : 'idle',
              state: status.hostReminders?.running ? 'On' : 'Idle',
              detail: status.hostReminders?.running
                ? (status.hostReminders?.lastRun
                  ? `Last run ${formatDate(status.hostReminders.lastRun)}`
                  : 'Scheduled, not run yet')
                : 'Hosts are not being reminded at 7, 3 or 1 day out.'
            },
            {
              key: 'wordpress',
              label: 'WordPress API',
              on: Boolean(status.wordpressApi?.enabled && status.wordpressApi?.configured),
              // Disabled on purpose is not a fault, so it stays grey rather than amber
              tone: status.wordpressApi?.enabled
                ? (status.wordpressApi?.configured ? 'on' : 'idle')
                : 'off',
              state: status.wordpressApi?.enabled
                ? (status.wordpressApi?.configured ? 'On' : 'Unset')
                : 'Off',
              detail: status.wordpressApi?.enabled
                ? (status.wordpressApi?.configured
                  ? 'Events sync to the public calendar.'
                  : 'Enabled, but the credentials are missing.')
                : 'Not configured in this environment.'
            }
          ].map((row) => (
            <div key={row.key} className="automation-row">
              <span
                className={`automation-row__dot automation-row__dot--${row.tone}`}
                aria-hidden="true"
              />
              <div className="automation-row__main">
                <span className="automation-row__label">{row.label}</span>
                <span className="automation-row__detail">{row.detail}</span>
              </div>
              <span className={`automation-row__state automation-row__state--${row.tone}`}>
                {row.state}
              </span>
            </div>
          ))}
        </DSCard>
      )}

      {/* Run manually: a list of actions, each a row with its own button. */}
      <DSCard frame="bars" flush headerTone="strong" cornerVariant="md" title="Run manually" className="automation-manual">
        {[
          {
            key: 'generate',
            label: 'Generate events',
            detail: 'From all active series, next 2 months',
            busyLabel: 'Running...',
            onClick: handleGenerateEvents,
            blocked: false
          },
          {
            key: 'reminders',
            label: 'Check reminders',
            detail: 'Send pending host reminders, 7d, 3d, 1d',
            busyLabel: 'Running...',
            onClick: handleCheckReminders,
            blocked: false
          },
          {
            key: 'wpsync',
            label: 'Sync to WordPress',
            detail: 'Push events to WordPress via REST API',
            busyLabel: 'Running...',
            onClick: handleWordPressSync,
            blocked: !status?.wordpressApi?.enabled
          },
          {
            key: 'orphans',
            label: 'Clean up WordPress',
            detail: 'Find events on the website that no longer exist here',
            busyLabel: 'Checking...',
            onClick: handleWordPressOrphans,
            blocked: !status?.wordpressApi?.enabled
          }
        ].map((row) => (
          <div key={row.key} className="automation-row">
            <div className="automation-row__main">
              <span className="automation-row__label">{row.label}</span>
              <span className="automation-row__detail">{row.detail}</span>
            </div>
            <DSButton
              appearance="solid"
              tone={row.blocked ? 'neutral' : 'secondary'}
              size="sm"
              onClick={row.onClick}
              disabled={processing === row.key || row.blocked}
            >
              {processing === row.key ? row.busyLabel : (row.blocked ? 'Blocked' : 'Run')}
            </DSButton>
          </div>
        ))}
      </DSCard>

      {/* The one thing that silently stops everything working, named, with the variable
          to set and a way to get it onto the clipboard. */}
      {status && !status.wordpressApi?.enabled && (
        <div className="automation-debug">
          <p className="automation-debug__title">WordPress sync is off</p>
          <p className="automation-debug__detail">
            API sync disabled. Enable <code className="automation-env">WP_API_ENABLED</code> in environment.
          </p>
          <DSButton
            appearance="outline"
            tone="neutral"
            size="sm"
            onClick={handleCopyEnvVar}
          >
            Copy variable name
          </DSButton>
        </div>
      )}

      {status && status.wordpressApi?.enabled && !status.wordpressApi?.configured && (
        <div className="automation-debug">
          <p className="automation-debug__title">WordPress credentials missing</p>
          <p className="automation-debug__detail">
            Set <code className="automation-env">WP_USERNAME</code> and{' '}
            <code className="automation-env">WP_APP_PASSWORD</code> on the server.
          </p>
        </div>
      )}

      {orphanState && (
        <WordPressOrphanModal
          orphans={orphanState.orphans}
          scanned={orphanState.scanned}
          submitting={orphanState.submitting}
          onCancel={() => setOrphanState(null)}
          onRemove={handleRemoveOrphans}
        />
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={processing === 'generate'}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default AutomationSettings;
